import { isAxiosError } from "axios";
import { t } from "@lingui/core/macro";

/**
 * The server puts its user-facing, localized text in `{ error }`. An
 * AxiosError's own `message` is the untranslated "Request failed with status
 * code 403", so it must never reach a toast — callers fall back to their own
 * copy when this returns undefined.
 */
export function getApiErrorMessage(error: unknown): string | undefined {
  if (isAxiosError(error)) {
    const serverMessage = error.response?.data?.error;

    if (typeof serverMessage === "string" && serverMessage.length > 0) {
      return serverMessage;
    }

    // A 413 is answered by the proxy in front of the API, so its body is HTML
    // with no `{ error }` — without this the upload fails with no reason shown.
    if (error.response?.status === 413) {
      return t`Datoteka je prevelika. Naložite manjšo datoteko.`;
    }

    return undefined;
  }

  return error instanceof Error && error.message ? error.message : undefined;
}

/**
 * `callbackWrapper` sends `code`; `useCustomMutation` reads `errorCode`. Accept
 * either so a code is found whichever the endpoint used.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!isAxiosError(error)) return undefined;

  const code = error.response?.data?.code ?? error.response?.data?.errorCode;

  return typeof code === "string" && code.length > 0 ? code : undefined;
}

/**
 * The maintenance gate and the proxy answer 503 in plain text; an endpoint's
 * own 503 (a query timeout, a busy reader) carries `{ error }` and is no outage.
 */
export function isMaintenanceResponse(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 503) return false;

  return typeof error.response?.data?.error !== "string";
}

const DEFAULT_QUERY_RETRIES = 3;

// The server already killed a timed-out query; asking again repeats the full wait.
export function retryUnlessQueryTimedOut(
  failureCount: number,
  error: unknown,
): boolean {
  return (
    getApiErrorCode(error) !== "QUERY_TIMEOUT" &&
    failureCount < DEFAULT_QUERY_RETRIES
  );
}

/**
 * Splits an API error across a toast's two slots. A toast truncates its title,
 * so the server's sentence belongs in the description with the short code above
 * it.
 */
export function getApiErrorToast(
  error: unknown,
  fallbackTitle: string,
): { title: string; description?: string } {
  const message = getApiErrorMessage(error);

  if (!message) return { title: fallbackTitle };

  return {
    title: getApiErrorCode(error) ?? fallbackTitle,
    description: message,
  };
}
